<template>
    <div class="user-activity-section">
        <div class="section-divider">
            <span class="section-title">User Activity</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Programs by Unique Users</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="userRankChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">User Activity Over Time</div>
                    <div class="panel-subtitle">Top programs + Others</div>
                </div>
                <div class="panel-body chart-extra-tall">
                    <canvas ref="userTimeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">User Engagement Comparison</div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Program</th>
                                    <th class="text-end">Users</th>
                                    <th class="text-end">Txns</th>
                                    <th class="text-end">Txns/User</th>
                                    <th class="text-end">Fees/User</th>
                                    <th class="text-end">CU/User</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="p in topByUsers" :key="p.program">
                                    <td>
                                        <a :href="`/analytics/programs/${p.program}`" class="text-gradient">
                                            {{ programLabel(p) }}
                                        </a>
                                    </td>
                                    <td class="text-end font-mono">{{ formatCompact(p.metrics.uniqueUsers) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(p.metrics.totalTransactions) }}</td>
                                    <td class="text-end font-mono">{{ txPerUser(p) }}</td>
                                    <td class="text-end font-mono">{{ feesPerUser(p) }} SOL</td>
                                    <td class="text-end font-mono">{{ formatCompact(cuPerUser(p)) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatPubkey, programLabel } from '../../composables/useFormatters';
import { colors, getBarChartOptions, getLineChartOptions } from '../../composables/useChartConfig';
import { GRANULARITY_MODES, transformValue, getUnitSuffix } from '../../composables/useGranularityToggle';

export default {
    name: 'ProgramUserActivity',

    props: {
        programs: {
            type: Array,
            required: true
        },
        interval: {
            type: String,
            default: '1h'
        },
        granularityMode: {
            type: String,
            default: GRANULARITY_MODES.TOTAL
        }
    },

    data() {
        return {
            charts: {},
            colors: colors
        };
    },

    computed: {
        topByUsers() {
            return [...this.programs]
                .sort((a, b) => b.metrics.uniqueUsers - a.metrics.uniqueUsers)
                .slice(0, 10);
        }
    },

    watch: {
        programs: {
            handler() {
                if (Object.keys(this.charts).length > 0) {
                    this.updateCharts();
                }
            },
            deep: true
        },
        granularityMode() {
            if (Object.keys(this.charts).length > 0) {
                this.updateCharts();
            }
        }
    },

    mounted() {
        this.initCharts();
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    methods: {
        formatCompact,
        formatPubkey,
        programLabel,

        transformForGranularity(value) {
            return transformValue(value, this.granularityMode, this.interval);
        },

        getGranularityLabel(baseLabel) {
            const suffix = getUnitSuffix(this.granularityMode, this.interval, '');
            return suffix ? `${baseLabel} ${suffix}` : baseLabel;
        },

        txPerUser(p) {
            if (!p.metrics.uniqueUsers) return '0';
            return (p.metrics.totalTransactions / p.metrics.uniqueUsers).toFixed(1);
        },

        feesPerUser(p) {
            if (!p.metrics.uniqueUsers) return '0';
            return (p.metrics.totalFees / p.metrics.uniqueUsers).toFixed(4);
        },

        cuPerUser(p) {
            if (!p.metrics.uniqueUsers) return 0;
            return p.metrics.totalCompute / p.metrics.uniqueUsers;
        },

        initCharts() {
            // User Rank Chart
            this.charts.userRank = markRaw(new Chart(this.$refs.userRankChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false,
                    formatCallback: v => formatCompact(v)
                })
            }));

            // User Time Chart
            this.charts.userTime = markRaw(new Chart(this.$refs.userTimeChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.updateCharts();
        },

        updateCharts() {
            if (!this.programs.length) return;

            const byUsers = this.topByUsers;
            const topN = [...this.programs]
                .sort((a, b) => b.metrics.uniqueUsers - a.metrics.uniqueUsers)
                .slice(0, 25); // Top 25 for time series
            const timeLabels = topN[0]?.timeSeries?.map(t => t.label) || [];
            const transform = this.transformForGranularity.bind(this);

            // User Rank - add borderWidth: 0
            this.charts.userRank.data = {
                labels: byUsers.map(p => programLabel(p)),
                datasets: [{
                    label: this.getGranularityLabel('Users'),
                    data: byUsers.map(p => transform(p.metrics.uniqueUsers)),
                    backgroundColor: this.colors.series,
                    borderWidth: 0
                }]
            };
            this.charts.userRank.update();

            // User Time - 25 programs + Others with gradient fills
            const othersData = timeLabels.map((_, timeIdx) => {
                const topNTotal = topN.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.users || 0);
                }, 0);
                const allTotal = this.programs.reduce((sum, p) => {
                    return sum + (p.timeSeries?.[timeIdx]?.users || 0);
                }, 0);
                return transform(Math.max(0, allTotal - topNTotal));
            });

            const ctx = this.$refs.userTimeChart.getContext('2d');
            const datasets = topN.map((p, i) => {
                const color = this.colors.series[i % this.colors.series.length];
                return {
                    label: programLabel(p),
                    data: p.timeSeries?.map(t => transform(t.users)) || [],
                    borderColor: color,
                    backgroundColor: `${color}33`,
                    fill: true,
                    tension: 0.3
                };
            });

            // Add "Others" category
            if (othersData.some(v => v > 0)) {
                datasets.push({
                    label: 'Others',
                    data: othersData,
                    borderColor: '#4b5563',
                    backgroundColor: '#4b556333',
                    fill: true,
                    tension: 0.3
                });
            }

            this.charts.userTime.data = {
                labels: timeLabels,
                datasets: datasets
            };
            this.charts.userTime.update();
        }
    }
};
</script>

<style scoped>
.chart-tall {
    min-height: 280px;
}
.font-mono {
    font-family: 'JetBrains Mono', monospace;
}
.text-gradient {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}
</style>
